import { stripDuplicateTrailingHashtags } from '@mondaygpt/constants'
import { Heart, MessageCircle, Share2, Bookmark, Music, Video } from 'lucide-react'
import { selectCurrentBusiness } from 'modules/business/slices/business-entities.slice'
import { useAppSelector } from 'modules/core'
import { useBrandKitStore, selectBrandLogo } from '../store/brand-kit-store'
import type { TikTokVideoContent } from '../types'
import { GeneratingMediaOverlay, GeneratingVideoPoster } from '../components/AssetMedia'
import { PreviewVideo } from './PreviewVideo'
import { VideoBeatCanvas } from '../composition/editor/VideoTimeline'
import { useVideoTimeline } from '../composition/editor/CompositionEditorProvider'
import {
  isGeneratingVideoFromFields,
  showPreviewCardGeneratingOverlay,
  toSocialHandle,
} from './preview-utils'

const SKEL = 'rgba(255, 255, 255, 0.10)'

function isTikTokVideoEmpty(content: TikTokVideoContent): boolean {
  return (
    !content.videoUrl &&
    !content.videoAttachmentId &&
    !content.caption &&
    (content.hashtags?.length ?? 0) === 0
  )
}

interface TikTokVideoPreviewProps {
  content: TikTokVideoContent
  generatingFields: Set<string>
  /** Post-scoped key for shared mute state. See PreviewVideo. */
  postKey: string
  /** When false, the timeline renders read-only (falls back to PreviewVideo). Defaults to true. */
  interactive?: boolean
}

export function TikTokVideoPreview({
  content,
  generatingFields,
  postKey,
  interactive = true,
}: TikTokVideoPreviewProps) {
  const business = useAppSelector(selectCurrentBusiness)
  const brandLogo = useBrandKitStore(selectBrandLogo)
  const businessName = business?.name || 'Your Business'
  // When the reel has an editable video Composition (provided by the composer), the media region
  // becomes the live editable timeline (beats + per-beat overlay editor). Falls back to the
  // read-only PreviewVideo for legacy reels / mobile / non-interactive previews.
  const timeline = useVideoTimeline()
  const canvasTimeline = !!timeline && interactive

  if (isTikTokVideoEmpty(content) && generatingFields.size === 0) {
    return <TikTokSkeletonPreview />
  }

  const caption = stripDuplicateTrailingHashtags(content.caption, content.hashtags)
  const isGeneratingVideo = isGeneratingVideoFromFields(content, generatingFields)
  const isGeneratingCaption = generatingFields.has('caption')
  const isGeneratingHashtags = generatingFields.has('hashtags')
  const isGenerating = generatingFields.size > 0
  const posterUrl = content.imageUrl
  const showCardOverlay = showPreviewCardGeneratingOverlay(
    isGenerating,
    isGeneratingVideo,
    posterUrl
  )

  // Editable video: the media region is the ACTIVE beat's live canvas; the beat track + controls
  // live in the composer's full-width bottom dock (ReelTimelineDock in ComposerShell).
  if (canvasTimeline) {
    return (
      <div className="relative mx-auto w-full max-w-[400px]">
        {showCardOverlay && <GeneratingMediaOverlay shape="video" tone="dark" radius={18} />}
        <VideoBeatCanvas />
      </div>
    )
  }

  return (
    <div
      className="relative mx-auto w-[320px] overflow-hidden rounded-2xl bg-black shadow-xl"
      style={{ aspectRatio: '9/16' }}
    >
      {showCardOverlay && <GeneratingMediaOverlay shape="video" tone="dark" radius={16} />}
      {/* Video area */}
      <div className="absolute inset-0">
        {content.videoUrl ? (
          <PreviewVideo
            src={content.videoUrl}
            postKey={postKey}
            className="h-full w-full object-cover"
          />
        ) : isGeneratingVideo && content.imageUrl ? (
          <GeneratingVideoPoster
            imageUrl={content.imageUrl}
            tone="dark"
            className="h-full w-full"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-b from-gray-800 to-gray-900">
            {!isGeneratingVideo && (
              <div className="flex flex-col items-center gap-2">
                <Video size={32} className="text-white/30" />
                <span className="text-xs text-white/30">Video preview</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Right side actions */}
      <div className="absolute right-3 bottom-32 flex flex-col items-center gap-5">
        {/* Profile */}
        <div className="relative">
          <div className="h-10 w-10 overflow-hidden rounded-full border-2 border-white">
            {brandLogo ? (
              <img src={brandLogo} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-primary text-xs font-bold text-white">
                {businessName.charAt(0)}
              </div>
            )}
          </div>
          <div className="absolute -bottom-1.5 left-1/2 flex h-4 w-4 -translate-x-1/2 items-center justify-center rounded-full bg-[#FE2C55] text-[10px] font-bold text-white">
            +
          </div>
        </div>

        <ActionButton icon={Heart} count="0" />
        <ActionButton icon={MessageCircle} count="0" />
        <ActionButton icon={Bookmark} count="0" />
        <ActionButton icon={Share2} count="0" />

        {/* Music disc */}
        <div
          className="h-8 w-8 animate-spin rounded-full border-2 border-gray-600 bg-gray-800"
          style={{ animationDuration: '3s' }}
        >
          <div className="flex h-full w-full items-center justify-center">
            <Music size={10} className="text-white/60" />
          </div>
        </div>
      </div>

      {/* Bottom content overlay */}
      <div className="absolute right-12 bottom-0 left-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 pt-16">
        {/* Username */}
        <div className="mb-2 flex items-center gap-1.5">
          <span className="text-sm font-bold text-white">@{toSocialHandle(businessName)}</span>
        </div>

        {/* Caption */}
        <div className="relative mb-2">
          {caption ? (
            <p className="text-xs leading-relaxed text-white/90">
              {caption.length > 150 ? caption.slice(0, 150) + '...' : caption}
            </p>
          ) : (
            <div
              className="h-4 w-3/4 rounded"
              style={{ background: isGeneratingCaption ? SKEL : 'rgba(255,255,255,0.1)' }}
            />
          )}
        </div>

        {/* Hashtags */}
        <div className="relative">
          {(content.hashtags?.length ?? 0) > 0 ? (
            <p className="text-xs text-white/70">
              {(content.hashtags ?? []).map(h => `#${h}`).join(' ')}
            </p>
          ) : isGeneratingHashtags ? (
            <div className="h-3.5 w-1/2 rounded" style={{ background: SKEL }} />
          ) : null}
        </div>

        {/* Music ticker */}
        <div className="mt-2 flex items-center gap-1.5">
          <Music size={10} className="text-white/60" />
          <span className="text-[10px] text-white/60">Original sound - {businessName}</span>
        </div>
      </div>
    </div>
  )
}

function ActionButton({ icon: Icon, count }: { icon: typeof Heart; count: string }) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <Icon size={24} className="text-white" />
      <span className="text-[10px] text-white">{count}</span>
    </div>
  )
}

function TikTokSkeletonPreview() {
  return (
    <div
      className="relative mx-auto w-[320px] overflow-hidden rounded-2xl bg-gradient-to-b from-gray-100 to-gray-200 shadow-xl"
      style={{ aspectRatio: '9/16' }}
    >
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Video size={40} className="text-gray-300" />
          <span className="text-sm text-gray-400">Your TikTok video preview will appear here</span>
          <span className="text-xs text-gray-300">Start chatting to create your video</span>
        </div>
      </div>
    </div>
  )
}
